// shabba-dashboard/src/components/analytics/AnalyticsCard.tsx

import React from 'react';
import { TrendingUp, TrendingDown, Clock, AlertTriangle, CheckCircle } from 'lucide-react';

interface AnalyticsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
  status?: 'good' | 'warning' | 'danger' | 'neutral';
  icon?: React.ReactNode;
  color?: string;
  children?: React.ReactNode;
}

export function AnalyticsCard({
  title,
  value,
  subtitle,
  trend,
  status = 'neutral',
  icon,
  color,
  children
}: AnalyticsCardProps) {
  const getStatusColor = () => {
    switch (status) {
      case 'good': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'danger': return '#ef4444';
      default: return color || '#94a3b8';
    }
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'up': return <TrendingUp size={12} style={{ color: '#10b981' }} />;
      case 'down': return <TrendingDown size={12} style={{ color: '#ef4444' }} />;
      default: return null;
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'good': return <CheckCircle size={14} style={{ color: '#10b981' }} />;
      case 'warning': return <AlertTriangle size={14} style={{ color: '#f59e0b' }} />;
      case 'danger': return <AlertTriangle size={14} style={{ color: '#ef4444' }} />;
      default: return null;
    }
  };

  return (
    <div style={{
      backgroundColor: '#1e293b',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #334155',
      position: 'relative'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {icon}
          <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>
            {title}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {getTrendIcon()}
          {getStatusIcon()}
        </div>
      </div>
      
      <div style={{ 
        fontSize: '20px', 
        fontWeight: '700', 
        color: getStatusColor(),
        marginBottom: subtitle ? '4px' : '0'
      }}>
        {value}
      </div>
      
      {subtitle && (
        <div style={{ fontSize: '11px', color: '#94a3b8' }}>
          {subtitle}
        </div>
      )}
      
      {children && (
        <div style={{ marginTop: '12px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

export function AnalyticsGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
      gap: '16px',
      marginBottom: '16px'
    }}>
      {children}
    </div>
  );
}

export function AnalyticsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ 
        margin: '0 0 16px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#e2e8f0',
        borderBottom: '1px solid #334155',
        paddingBottom: '8px'
      }}>
        {title}
      </h3>
      {children}
    </div>
  );
}
